// Lib
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';

// Utils
import { getPublicLink } from '../publishedBoardUtils';
import {
    getIsPublishedFeedbackEnabled,
    getTitle,
    getIsPublishedMediaDownloadDisabled,
} from '../../../../../../../../common/elements/utils/elementPropertyUtils';
import { validateBoardTitle } from '../../../boardSharingValidationUtils';
import { isPlatformPhoneOrMobileMode } from '../../../../../../../platform/utils/platformDetailsUtils';

// Actions
import { publishBoard, unpublishBoard } from '../publishBoardActions';

// Selectors
import { currentBoardIsPublishedSelector } from '../../../../../../../utils/permissions/elementPermissionsSelector';
import { getPlatformDetailsSelector } from '../../../../../../../platform/platformSelector';

// Components
import PublicBoardURLField from '../../../../PublicBoardURLField';
import ToggleInput from '../../../../../../../components/form/fields/ToggleInput';
import TooltipSource from '../../../../../../../components/tooltips/TooltipSource';
import ToggleContents from '../../../../../../../components/form/fields/ToggleContents';
import StaticURLField from '../../../../../../../components/form/fields/StaticURLField';
import SettingsRow from '../../../../../../../user/account/accountModal/components/SettingsRow';

// Constants
import { TIMES } from '../../../../../../../../common/utils/timeUtil';
import { TooltipPositions } from '../../../../../../../components/tooltips/tooltipConstants';

// Styles
import './PublishLinkToggle.scss';

const PublishLinkURLField = (props) => {
    const {
        hasLegacyPublicPermissions,
        currentBoardId,
        currentBoardPublishedPermissionId,
        error,
        errorMessage,
        showSpinner,
    } = props;

    if (error) {
        return <StaticURLField url={errorMessage} className="error-text" />;
    }

    if (!hasLegacyPublicPermissions) {
        return (
            <PublicBoardURLField
                currentBoardId={currentBoardId}
                permissionsId={currentBoardPublishedPermissionId}
                showSpinner={showSpinner}
            />
        );
    }

    return <StaticURLField url={getPublicLink(currentBoardId)} showSpinner={showSpinner} />;
};

PublishLinkURLField.propTypes = {
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    errorMessage: PropTypes.string,
    hasLegacyPublicPermissions: PropTypes.bool,
    currentBoardId: PropTypes.string,
    currentBoardPublishedPermissionId: PropTypes.string,
    showSpinner: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
    dispatchPublishBoard: (args) => dispatch(publishBoard(args)),
    dispatchUnpublishBoard: ({ boardId }) => dispatch(unpublishBoard({ boardId })),
});

const PublishLinkToggle = (props) => {
    const {
        publishLinkEnabled,
        setPublishLinkEnabled,
        currentBoardPublishedPermissionId,

        canEditLink,
        showSharedAncestorBoardLink,

        publishedAncestorBoard,
        currentBoardId,
        currentBoard,

        dispatchPublishBoard,
        dispatchUnpublishBoard,
        setErrorToast,
    } = props;

    const [isLoading, setIsLoading] = React.useState(false);
    const [isLongLoading, setIsLongLoading] = React.useState(false);

    // The response to the publish web request can be inaccurate when
    // there's server instability, as there're some knock-on effects that
    // have to happen too. Reading isPublished from redux means it's 100%
    // accurate to the board's actual state.
    const isPublished = useSelector(currentBoardIsPublishedSelector);
    const platformDetails = useSelector(getPlatformDetailsSelector);

    useEffect(() => {
        // if the actual publish state matches the publish intent, we're good
        if (isPublished === publishLinkEnabled) {
            setIsLongLoading(false);
            return () => null;
        }
        // if there is a mismatch for more than a moment, show a loading spinner
        const timeout = setTimeout(() => {
            setIsLongLoading(true);
        }, 1800);
        return () => clearTimeout(timeout);
    }, [isPublished, publishLinkEnabled]);

    const onTogglePublish = async (event) => {
        const nowEnabled = event.target.checked;

        setIsLoading(true);

        // Validate board title before enabling read-only link
        if (nowEnabled) {
            const boardTitle = getTitle(currentBoard);
            const isMobile = isPlatformPhoneOrMobileMode(platformDetails);
            const errorMessage = validateBoardTitle(boardTitle, isMobile);

            if (errorMessage) {
                setErrorToast(errorMessage);
                setIsLoading(false);
                return;
            }
        }

        setPublishLinkEnabled(nowEnabled);

        let publishPromise;

        if (nowEnabled) {
            const commentsEnabled = getIsPublishedFeedbackEnabled(currentBoard) !== false;
            const downloadFilesDisabled = getIsPublishedMediaDownloadDisabled(currentBoard) !== true;

            publishPromise = dispatchPublishBoard({ boardId: currentBoardId, commentsEnabled, downloadFilesDisabled });
        } else {
            publishPromise = dispatchUnpublishBoard({ boardId: currentBoardId });
        }

        try {
            await publishPromise;
        } catch (err) {
            const defaultMessage = nowEnabled ? 'Failed to create read-only link' : 'Failed to turn off read-only link';
            setErrorToast(defaultMessage);
        }

        setIsLoading(false);
    };

    return (
        <div className="PublishLinkToggle">
            <SettingsRow loading={isLoading} className="strong" label="Enable read-only link">
                <TooltipSource
                    className="share-link-toggle-tooltip small-text"
                    enabled={!!showSharedAncestorBoardLink}
                    triggerOnClick
                    position={TooltipPositions.BOTTOM}
                    duration={TIMES.SECOND * 3}
                    offset={-8}
                    tooltipText={`Change this setting in: ${getTitle(publishedAncestorBoard)}`}
                >
                    <ToggleInput
                        className="publish-link-toggle-input"
                        active={publishLinkEnabled}
                        disabled={!canEditLink || showSharedAncestorBoardLink}
                        onChange={onTogglePublish}
                    />
                </TooltipSource>
            </SettingsRow>
            <ToggleContents
                active
                loading={isLoading}
                disabled={!(publishLinkEnabled && !!currentBoardPublishedPermissionId)}
            >
                <div className="setting-contents">
                    <PublishLinkURLField {...props} showSpinner={isLongLoading} />
                </div>
            </ToggleContents>
        </div>
    );
};

PublishLinkToggle.propTypes = {
    publishLinkEnabled: PropTypes.bool,
    setPublishLinkEnabled: PropTypes.func,

    canEditLink: PropTypes.bool,
    canModifySettings: PropTypes.bool,
    hasLegacyPublicPermissions: PropTypes.bool,
    showSharedAncestorBoardLink: PropTypes.bool,

    currentBoard: PropTypes.object,
    currentBoardId: PropTypes.string,
    publishedAncestorBoardId: PropTypes.string,
    publishedAncestorBoard: PropTypes.object,
    currentBoardPublishedPermissionId: PropTypes.string,

    dispatchPublishBoard: PropTypes.func,
    dispatchUnpublishBoard: PropTypes.func,
    setErrorToast: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(PublishLinkToggle);
